import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { parseApiError } from '../../lib/apiErrors';

export const usePostSubscription = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [subscription, setSubscription] = useState(null);
    const { getAccessTokenSilently } = useAuth0();
    const apiUrl = process.env.REACT_APP_API_URL;

    const postSubscription = async (groupId) => {
        setIsLoading(true);
        setError('');

        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const response = await fetch(`${apiUrl}/api/subscriptions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    GroupId: groupId,
                    createdOn: new Date().toISOString(),
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setSubscription(data);
                return data;
            }

            const apiError = await parseApiError(response, 'Could not join this group.');
            const message = response.status === 409
                ? 'You are already a member of this group.'
                : apiError.message || `Membership could not be created (${response.status}).`;
            setError(message);
            throw new Error(message);
        } catch (error) {
            const message = error.message || 'Membership could not be created.';
            setError(message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteSubscription = async (subscriptionId) => {
        setIsLoading(true);
        setError('');

        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const response = await fetch(`${apiUrl}/api/subscriptions/${subscriptionId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok || response.status === 204) {
                setSubscription(null);
                return true;
            }

            const apiError = await parseApiError(response, 'Could not leave this group.');
            const message = apiError.message || `Membership could not be removed (${response.status}).`;
            setError(message);
            throw new Error(message);
        } catch (error) {
            const message = error.message || 'Membership could not be removed.';
            setError(message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    return { error, isLoading, postSubscription, deleteSubscription, subscription };
};
